import { MutableRefObject, useMemo, useRef, useState } from 'react';
import {
  DataGridPro,
  GridColDef,
  GridApiPro,
  GridDensity,
  GridRenderCellParams,
  GridSingleSelectColDef,
  GridSortDirection,
  GridValidRowModel,
  ValueOptions,
  getGridSingleSelectOperators,
  gridFilteredSortedRowIdsSelector,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { DefaultInventoryGridColumnDefs } from '../Inventory/inventoryGridColumnDefs';
import { DefaultCaseGridColumnDefs } from '../SupportCases/SupportCaseGridColDefs';
import { projectGridColumns } from '../Projects/projectGridColumnsDefs';
import { projectsRelatedInventoryColumns } from '../Projects/ProjectsRelatedInventoryGridColumnDefs';
import { WorkflowBaseColumns } from '../Orders/WorkflowOrdersGridColumnDefs';
// helpers must stay below the col-def imports: they call its operator builders at module eval.
import { populateColumnValueOptions } from '../../Compounds/UpstackDataGrid/helpers';
import { isProjectsUpdatesEnabled } from '../../../store/User';
import { useFeatureFlags } from '../../../Api/useFeatureFlags';
import { sanitizeFilterModelItems } from '../../Compounds/UpstackDataGrid/filterModelSanitizer';
import { TAGS_FIELD } from '../../Compounds/UpstackDataGrid/tagColumns';
import { isFieldVisible } from '../../Compounds/UpstackDataGrid/columnVisibility';
import { TagsDetails } from '../../Compounds/Tagging/TagsDetails';
import { Tag } from '../../../store/Tags';
import {
  CUSTOM_FIELD_COLUMN_PREFIX,
  createCustomFieldColumns
} from '../../Compounds/UpstackDataGrid/customFieldColumns';
import { CustomFieldValue } from '../../../types';
import { CustomFilterPanel } from '../../Compounds/UpstackDataGrid/CustomFilterPanel';
import { ReportViewConfig } from '../../../Api/Report/reportApi';
import { normalizeInventoryAsset } from '../../../store/Inventory';
import { InventoryAsset } from '../../../types';
import { DEFAULT_TABLE_STYLES } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { DD_GRID_CELL_SLOT_PROPS } from '../../../utils/telemetry';
import { FullReportToolbar, MinimalReportToolbar } from './ReportGridToolbar';

const DETAILS_FIELD = 'details';

// Dropping a column drops the saved view's filter and sort on it, which silently widens the report.
const isFilterOnlyColumn = (column: GridColDef): boolean =>
  column.field === TAGS_FIELD || !!(column as { defaultHidden?: boolean }).defaultHidden;

// A report shows what its view shows (the api lays the account's layout under it), except what the account disabled.
const reportVisibility = (
  columns: GridColDef[],
  disabledFields: readonly string[],
  columnVisibilityModel: Record<string, boolean> | undefined
): Record<string, boolean> =>
  Object.fromEntries(
    columns.map((column) => [
      column.field,
      isFieldVisible(
        column.field,
        (column as { defaultHidden?: boolean }).defaultHidden,
        disabledFields,
        columnVisibilityModel
      )
    ])
  );

export function buildColumnsFromData(content: Record<string, unknown>[], disabledFields: string[] = []): GridColDef[] {
  if (!content.length) return [];
  return Object.keys(content[0])
    .filter((key) => !disabledFields.includes(key))
    .map((key) => ({
      field: key,
      headerName: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      width: 150
    }));
}

export function getColumnsForViewType(
  viewType: string | undefined,
  content: Record<string, unknown>[],
  disabledFields: string[],
  projectsUpdatesEnabled: boolean = false
): GridColDef[] {
  // Hiding a column from a grid is a display choice; it must not widen an emailed report.
  const filter = (cols: GridColDef[]) => cols.filter((c) => isFilterOnlyColumn(c) || !disabledFields.includes(c.field));
  switch (viewType) {
    case 'asset':
    case 'inventory':
      return filter(DefaultInventoryGridColumnDefs as GridColDef[]);
    case 'case':
      return filter(DefaultCaseGridColumnDefs as GridColDef[]);
    case 'project':
      return filter(projectGridColumns(projectsUpdatesEnabled) as GridColDef[]);
    case 'related_project_assets':
      return filter(projectsRelatedInventoryColumns(true) as GridColDef[]);
    case 'order':
      return filter(WorkflowBaseColumns as GridColDef[]);
    default:
      return buildColumnsFromData(content, disabledFields);
  }
}

// Chips only: the grid renderer mounts the tag editor, which reads the account's tags and can write them.
const readOnlyTagsColumn = (column: GridColDef): GridColDef => ({
  ...column,
  cellClassName: undefined,
  renderCell: (params: GridRenderCellParams) => (
    <TagsDetails
      entityTags={(params.row?.tags as Tag[]) || []}
      isGrid
    />
  )
});

// Built from the rows (no session to ask) and kept whole: dropping a disabled column drops its saved filter.
const reportCustomFieldColumns = (rows: GridValidRowModel[]): GridColDef[] => {
  const carrier = rows.find((row) => ((row.custom_fields as CustomFieldValue[]) || []).length > 0);
  const defs = ((carrier?.custom_fields as CustomFieldValue[]) || []).map((field) => ({
    id: field.definition_id,
    name: field.name,
    field_type: field.field_type,
    position: field.position
  }));

  return createCustomFieldColumns(defs).map((column) => ({
    ...column,
    editable: false,
    valueSetter: undefined,
    // Declared rather than left to MUI's default, so the panel and the saved-operator check read one list.
    ...(column.type === 'singleSelect' ? { filterOperators: getGridSingleSelectOperators() } : {})
  })) as GridColDef[];
};

// A column that already names its own options knows them better than the rows do: a yes/no field
// carries Yes/No labels, and reading it off the rows would put the raw 'true'/'false' in the panel.
const declaresItsOwnOptions = (column: GridColDef): boolean => {
  const options = (column as GridSingleSelectColDef).valueOptions;
  if (typeof options === 'function') return true;

  return (options as ValueOptions[] | undefined)?.some((option) => typeof option === 'object') ?? false;
};

// Options come from the rows: without them the in-portal picker is empty and clears the filter on touch.
const withCustomFieldOptions = (columns: GridColDef[], rows: GridValidRowModel[]): GridColDef[] =>
  columns.map((column) => {
    if (column.type !== 'singleSelect' || declaresItsOwnOptions(column)) return column;

    const definitionId = column.field.slice(CUSTOM_FIELD_COLUMN_PREFIX.length);
    const values = rows
      .flatMap((row) => (row.custom_fields as CustomFieldValue[]) || [])
      .filter((field) => field.definition_id === definitionId && field.value)
      .map((field) => field.value as string);

    return { ...column, valueOptions: [...new Set(values)] };
  });

// The full (in-portal) variant populates singleSelect valueOptions from the report rows, like
// the grid pages do; the anonymous one-time page (minimal) has filtering disabled and skips it.
// Options are written onto copies so report data never leaks into the shared page col defs.
export const buildReportGrid = (
  viewType: string | undefined,
  content: Record<string, unknown>[],
  disabledFields: string[],
  isFull: boolean,
  projectsUpdatesEnabled: boolean = false
): { rows: GridValidRowModel[]; columns: GridColDef[] } => {
  const rawRows = content || [];
  // Related-inventory snapshots arrive raw like inventory does, and the live RI grids normalize them,
  // so the report has to as well or a saved view's filters read against labels the rows never carry.
  const normalizesRows = viewType === 'inventory' || viewType === 'asset' || viewType === 'related_project_assets';
  const rows = (
    normalizesRows ? (rawRows as unknown as InventoryAsset[]).map(normalizeInventoryAsset) : rawRows
  ) as GridValidRowModel[];
  const native = getColumnsForViewType(viewType, content, disabledFields, projectsUpdatesEnabled).map((column) =>
    column.field === TAGS_FIELD ? readOnlyTagsColumn(column) : { ...column }
  );
  // Appended after the native populate, like the grid pages: that step would wipe options it cannot read.
  const customFields = reportCustomFieldColumns(rows);
  return {
    rows,
    columns: isFull
      ? [
          ...populateColumnValueOptions(rows, native as GridSingleSelectColDef[]),
          ...withCustomFieldOptions(customFields, rows)
        ]
      : [...native, ...customFields]
  };
};

// GridFilterForm crashes on an unknown field, so a saved item still has to name a column we carry.
export const buildReportInitialState = (
  muiConfig: ReportViewConfig['muiConfig'] | undefined,
  columns: GridColDef[],
  disabledFields: readonly string[] = []
): GridInitialStatePro => {
  if (!muiConfig) {
    return {
      columns: {
        columnVisibilityModel: { ...reportVisibility(columns, disabledFields, undefined), [DETAILS_FIELD]: false }
      },
      pagination: { paginationModel: { page: 0, pageSize: 100 } }
    };
  }

  const savedFilter = muiConfig.filter as
    | { filterModel?: { items?: Array<{ field: string; operator: string }> } }
    | undefined;
  const savedItems = savedFilter?.filterModel?.items;
  const filter = savedItems
    ? {
        ...savedFilter,
        filterModel: {
          ...savedFilter?.filterModel,
          items: sanitizeFilterModelItems(savedItems, columns)
        }
      }
    : savedFilter;

  return {
    pinnedColumns: muiConfig.pinnedColumns,
    columns: {
      ...muiConfig.columns,
      columnVisibilityModel: {
        ...reportVisibility(columns, disabledFields, muiConfig.columns?.columnVisibilityModel),
        [DETAILS_FIELD]: false
      }
    },
    filter,
    density: (muiConfig.density as GridDensity) || 'standard',
    sorting: {
      sortModel: (muiConfig.sorting?.sortModel || []).map((item) => ({
        field: item.field,
        sort: item.sort as GridSortDirection
      }))
    },
    pagination: {
      paginationModel: muiConfig.pagination?.paginationModel || { page: 0, pageSize: 100 }
    }
  } as GridInitialStatePro;
};

export type ReportToolbarVariant = 'minimal' | 'full';

export interface ReportDataTableProps {
  viewType?: string;
  content: Record<string, unknown>[];
  viewConfig?: ReportViewConfig | null;
  viewName: string;
  toolbarVariant?: ReportToolbarVariant;
  // One-time page auto-downloads the CSV once the grid mounts.
  autoExportOnLoad?: boolean;
  onFilteredRowCountChange?: (count: number) => void;
  apiRef?: MutableRefObject<GridApiPro>;
  heightClass?: string;
}

/** The rows a grid shows after its filters, or null once the grid has unmounted and React has cleared its apiRef. */
export const filteredRowCount = (apiRef: MutableRefObject<GridApiPro>): number | null =>
  apiRef.current ? gridFilteredSortedRowIdsSelector(apiRef).length : null;

export const ReportDataTable = ({
  viewType,
  content,
  viewConfig,
  viewName,
  toolbarVariant = 'minimal',
  autoExportOnLoad = false,
  onFilteredRowCountChange,
  apiRef: externalApiRef,
  heightClass = 'h-[45rem]'
}: ReportDataTableProps) => {
  const ownApiRef = useGridApiRef();
  const apiRef = externalApiRef ?? ownApiRef;
  const downloadTriggered = useRef(false);
  const lastReportedCount = useRef<number | null>(null);
  const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);
  const isFull = toolbarVariant === 'full';
  const { config: featureFlags } = useFeatureFlags();
  const projectsUpdatesEnabled = isProjectsUpdatesEnabled(featureFlags);

  // Recomputed only when the report itself changes — building options scans every row per column.
  const { rows, columns, initialState } = useMemo(() => {
    const grid = buildReportGrid(
      viewType,
      content,
      viewConfig?.disabledFields || [],
      toolbarVariant === 'full',
      projectsUpdatesEnabled
    );
    return {
      ...grid,
      initialState: buildReportInitialState(viewConfig?.muiConfig, grid.columns, viewConfig?.disabledFields || [])
    };
  }, [viewType, content, viewConfig, toolbarVariant, projectsUpdatesEnabled]);

  return (
    <div className={heightClass}>
      <DataGridPro
        rows={rows}
        columns={columns}
        apiRef={apiRef}
        getRowId={(row) => row['id'] as string}
        columnHeaderHeight={DEFAULT_TABLE_STYLES.headerHeight}
        rowHeight={DEFAULT_TABLE_STYLES.rowHeight}
        sx={DEFAULT_TABLE_STYLES.sx}
        pagination
        initialState={initialState}
        pageSizeOptions={[50, 100, 200]}
        onStateChange={() => {
          const count = filteredRowCount(apiRef);
          if (count === null) return;
          if (onFilteredRowCountChange && lastReportedCount.current !== count) {
            lastReportedCount.current = count;
            onFilteredRowCountChange(count);
          }
          if (autoExportOnLoad && !downloadTriggered.current) {
            downloadTriggered.current = true;
            apiRef.current.exportDataAsCsv({ fileName: viewName });
          }
        }}
        disableColumnFilter={!isFull}
        disableColumnMenu
        disableColumnReorder={!isFull}
        disableRowSelectionOnClick
        slots={{ toolbar: isFull ? FullReportToolbar : MinimalReportToolbar, filterPanel: CustomFilterPanel }}
        slotProps={{
          cell: DD_GRID_CELL_SLOT_PROPS,
          toolbar: { viewName, setFilterButtonEl },
          ...(filterButtonEl ? { panel: { anchorEl: filterButtonEl } } : {})
        }}
      />
    </div>
  );
};
